import { LEVER_ID_VERIFICATION_VARIANT } from 'Lib/ConfigService/levers';
import { CACHE_STRATEGY_LRU, createDenormalizedSelector } from 'Lib/reduxUtils';
import { getLeverValue } from 'Lib/utils/getLeverValue';

import type { UserProfile } from 'Store/entities/types';
import type { SupportedBadge, User, UserBase } from 'Store/profile/user.types';

import { HEIGHT_MEASUREMENT_DESCRIPTOR } from 'Containers/EditProfile/common/constants';
import {
  ID_VERIFICATION_STATUS_NOT_VERIFIED,
  ID_VERIFICATION_STATUS_VERIFIED,
  PHOTO_VERIFICATION_STATUS_NOT_VERIFIED,
  PHOTO_VERIFICATION_STATUS_VERIFIED,
} from 'Containers/ModalManager/modals/common/FaceScanVerificationModal/constants';
import { getIsIdVerificationEnabled } from 'Containers/ModalManager/modals/SelfieVerificationV2/helpers';

import { PROFILE_ENTITY_KEY } from './entityKeys';
import { profileArray, profileEntity } from './profiles.schema';
import { createEntitiesSelector, createEntitiesTypeSelector } from './selectors';
import { getId, getIds } from './utils';

export const selectProfiles = createEntitiesSelector(PROFILE_ENTITY_KEY);
export const selectEntitiesForProfiles = createEntitiesTypeSelector([PROFILE_ENTITY_KEY]);

/**
 * Gets denormalized profile entities by ID
 *
 * @param  {GlobalState} state - top-level state
 * @param  {Array<string>} ids - entity IDs
 * @return {Array<UserProfile>} denormalized entities
 */
export const getProfilesByIds = createDenormalizedSelector(
  getIds,
  selectEntitiesForProfiles,
  profileArray,
  4
);

/**
 * Gets a denormalized profile entity by ID
 *
 * @param  {GlobalState} state - top-level state
 * @param  {string} id - entity ID
 * @return {User} denormalized entity
 */
export const getProfileById = createDenormalizedSelector(
  getId,
  selectEntitiesForProfiles,
  profileEntity,
  120,
  CACHE_STRATEGY_LRU
);

/**
 * Gets birthday from a profile object
 *
 * @param  {User} profile - user profile
 * @return {string} birthday
 */
export const getProfileBirthDate = (profile: UserBase) => profile.birth_date;

/**
 * Gets isSuperLike (whether a profile has superliked the current user)
 *
 * @param  {User} profile - user profile
 * @return {boolean} whether a profile has superliked the current user
 */
export const getIsSuperLike = (profile: UserProfile) => profile.is_super_like;

/**
 * Gets isBoostMatch (whether a profile was boosting)
 *
 * @param  {User} profile - user profile
 * @return {boolean} whether a profile was boosting
 */
export const getIsBoost = (profile: UserProfile) => profile.is_boost;

/**
 * Gets isSuperBoostMatch (whether a profile was super boosting)
 *
 * @param  {User} profile - user profile
 * @return {boolean} whether a profile was super boosting
 */
export const getIsSuperBoost = (profile: UserProfile) => profile.is_super_boost;

/**
 * Gets isVerified (whether a profile has a verified badge)
 *
 * @param  {User} profile - user profile
 * @return {boolean} whether a user has a verified badge
 */
const verifiedStatus = new Set([
  PHOTO_VERIFICATION_STATUS_VERIFIED,
  ID_VERIFICATION_STATUS_VERIFIED,
]);

export const getIsVerified = (profile?: UserProfile | User | null) => {
  if (!profile) {
    return false;
  }

  const idVerificationVariant = getLeverValue(LEVER_ID_VERIFICATION_VARIANT);
  const isIdVerificationEnabled = getIsIdVerificationEnabled(idVerificationVariant);
  if (!isIdVerificationEnabled) {
    return getIsPhotoVerified(profile) || false;
  }

  return (getIsPhotoVerified(profile) && getIsIdVerified(profile)) || false;
};

export const getIsPartiallyVerified = (profile?: UserProfile | User | null) => {
  if (!profile) {
    return false;
  }

  return (
    !getIsVerified(profile) && (getIsPhotoVerified(profile) || getIsIdVerified(profile) || false)
  );
};

export const getIsPhotoVerified = (profile?: UserProfile | User | null) => {
  if (!profile) {
    return false;
  }
  return verifiedStatus.has(getPhotoVerificationStatus(profile)) || false;
};

export const getIsIdVerified = (profile?: UserProfile | User | null) => {
  if (!profile) {
    return false;
  }
  return verifiedStatus.has(getIdVerificationStatus(profile)) || false;
};

export const getPhotoVerificationStatus = (profile?: UserProfile | User | null): SupportedBadge =>
  profile?.badges?.find((status) => status.type.startsWith('selfie_'))?.type ??
  PHOTO_VERIFICATION_STATUS_NOT_VERIFIED;

export const getIdVerificationStatus = (profile?: UserProfile | User | null): SupportedBadge =>
  profile?.badges?.find((status) => status.type.startsWith('id_dob_'))?.type ??
  ID_VERIFICATION_STATUS_NOT_VERIFIED;

export const getSpotifyAnthem = (profile: UserProfile) => profile.spotify?.spotify_theme_track;
export const getSpotifyTopArtists = (profile: UserProfile) => profile.spotify?.spotify_top_artists;

/**
 * Looks for profile's height information coming from selected descriptors filled by user.
 * @param profile
 * @returns Height information could also be undefined
 */
export const getHeightInformation = (profile: UserBase) =>
  profile?.selected_descriptors?.find(
    (descriptor) => descriptor.id === HEIGHT_MEASUREMENT_DESCRIPTOR
  );
